import { useMemo, forwardRef, useImperativeHandle } from "react"
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { PieChart } from "lucide-react"
import { OvertimeRequest } from "@/types"
import { OvertimeStatus } from "@/types/enums"
import ExportButton from "@/components/ui/ExportButton"
import DonutChart, { DonutChartData } from "@/components/ui/DonutChart"

interface ApprovalStatusDonutChartProps {
  requests: OvertimeRequest[]
  title?: string
  isLoading?: boolean
  standalone?: boolean
  hasActiveFilters?: boolean
}

interface ApprovalStatusDonutChartHandle {
  download: () => void;
}

const ApprovalStatusDonutChart = forwardRef<ApprovalStatusDonutChartHandle, ApprovalStatusDonutChartProps>(
  function ApprovalStatusDonutChart(
    { 
      requests, 
      title = "Approval Status",
      isLoading = false,
      standalone = false,
      hasActiveFilters = false
    },
    ref
  ) {

  // Process approval status data
  const statusData = useMemo(() => {
    if (!requests?.length) return [];

    const statusCounts = requests.reduce((acc, request) => {
      let status: string;
      if (request.status === OvertimeStatus.APPROVED) {
        status = 'Approved';
      } else if (request.status === OvertimeStatus.REJECTED) {
        status = 'Rejected';
      } else if (request.status === OvertimeStatus.PENDING || 
                 request.status === OvertimeStatus.PENDING_VP_APPROVAL ||
                 request.status === OvertimeStatus.PENDING_DH_APPROVAL ||
                 request.status === OvertimeStatus.PENDING_PM_APPROVAL) {
        status = 'Pending';
      } else {
        // Handle any other statuses (like cancelled) as 'Other'
        status = 'Other';
      }
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate totals for percentages
    const total = requests.length;
    
    return Object.entries(statusCounts)
      .filter(([, count]) => count > 0) // Only include statuses that exist
      .map(([status, count]) => ({
        name: status,
        value: count,
        percentage: ((count / total) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value); // Sort by count (descending)
  }, [requests]);

  // Convert data for DonutChart component
  const donutChartData = useMemo<DonutChartData[]>(() => {
    return statusData.map((item) => {
      const colorMap = {
        'Approved': 'hsl(var(--chart-2))', // Green
        'Rejected': 'hsl(var(--chart-1))', // Red
        'Pending': 'hsl(var(--chart-3))',  // Orange
        'Other': 'hsl(var(--chart-4))'     // Gray
      };
      
      return {
        name: item.name,
        value: item.value,
        fill: colorMap[item.name as keyof typeof colorMap] || 'hsl(var(--chart-5))'
      };
    });
  }, [statusData]);

  // Create chart config for DonutChart
  const donutChartConfig = useMemo(() => {
    const config: Record<string, { label: string; color?: string }> = {
      value: {
        label: "Requests",
      },
    };
    
    statusData.forEach((item, index) => {
      const colorMap = {
        'Approved': 'hsl(var(--chart-2))', // Green
        'Rejected': 'hsl(var(--chart-1))', // Red
        'Pending': 'hsl(var(--chart-3))',  // Orange
        'Other': 'hsl(var(--chart-4))'     // Gray
      };
      
      config[item.name] = {
        label: item.name,
        color: colorMap[item.name as keyof typeof colorMap] || `hsl(var(--chart-${(index % 20) + 1}))`,
      };
    });
    
    return config;
  }, [statusData]);

  // Calculate approval rate for center label
  const approvalRate = useMemo(() => {
    if (!requests?.length) return 0;
    const approvedCount = requests.filter(req => req.status === OvertimeStatus.APPROVED).length;
    return ((approvedCount / requests.length) * 100).toFixed(1);
  }, [requests]);

  // Handle CSV download
  const handleDownload = () => {
    if (!statusData.length) return;

    const headers = ["Status", "Count", "Percentage"];
    const csvData = statusData.map(item => [
      item.name,
      item.value.toString(),
      `${item.percentage}%`
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `approval_status_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Chart content
  const chartContent = (
    <div className="h-72">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse h-40 w-full bg-muted rounded-lg" />
        </div>
      ) : (
        <DonutChart
          data={donutChartData}
          chartConfig={donutChartConfig}
          isLoading={isLoading}
          showLegend={true}
          showCenterLabel={true}
          centerLabelValue={`${approvalRate}%`}
          centerLabelSubtext="Approval Rate"
          formatValue={(value) => `${value} requests`}
          height={288}
          className="flex items-center justify-center"
        />
      )}
    </div>
  );
    
  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  // Render with or without Card container based on standalone prop
  if (standalone) {
    return chartContent;
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-3 px-6">
        <CardTitle className="text-lg font-semibold text-left flex items-center">
          <PieChart className="w-4 h-4 mr-2 text-primary" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton 
            onClick={handleDownload} 
            disabled={!statusData.length || isLoading} 
          />
        </div>
      </CardHeader>
      <CardContent className="pt-2 pb-4 flex-1 flex flex-col">
        <div className="h-72 flex-1">
          {donutChartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <PieChart className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {hasActiveFilters ? "No data matches your filters" : "No approval data available"}
              </p>
              <p className="text-sm max-w-xs">
                {hasActiveFilters 
                  ? "Try adjusting the filters to see data"
                  : "Data will appear here once overtime requests are processed"
                }
              </p>
            </div>
          ) : (
            <DonutChart
              data={donutChartData}
              chartConfig={donutChartConfig}
              isLoading={isLoading}
              showLegend={true}
              showCenterLabel={true}
              centerLabelValue={`${approvalRate}%`}
              centerLabelSubtext="Approval Rate"
              formatValue={(value) => `${value} requests`}
              height={288}
              innerRadius={80}
              outerRadius={120}
              className="flex items-center justify-center"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default ApprovalStatusDonutChart; 